import {
  DashboardDataTransformer,
  DistrictIdNameMappingDashboardDataTransformer,
  IndustryIdNameMappingDashboardDataTransformer,
  StateIdCodeMappingDashboardDataTransformer,
  StateIdNameMappingDashboardDataTransformer,
  SubMarketIdNameMappingDashboardDataTransformer,
  WzIdNameMappingDashboardDataTransformer,
} from '../../data_transformers/dashboard_data_transformer';
import { DashboardConfigurationDependencies, Granularity } from '../dashboard-config.model';

/**
 * Dataset-level properties shared by several granularities. Availability rules
 * (e.g. a KPI missing at district level) are keyed by trait, not by granularity list.
 */
export type GranularityTrait = 'district-level' | 'wz';

/**
 * Id → name columns a granularity's rows carry. Each mapper reads its own id column and
 * writes its own name column (a no-op when the id is absent), so their order is irrelevant.
 */
export type IdNameMapping = 'stateCode' | 'stateName' | 'subMarket' | 'industry' | 'district' | 'wz';

/** The grouped rows / bars: the multi-select filter choosing them and the columns naming them. */
export interface GranularityGroup {
  filterName: string;
  valueKey: string;
  /** Short label (state code, WZ code); absent where the name is the only label. */
  codeKey?: string;
  nameKey: string;
  /** The code means nothing alone ('10.1'), so a view labelled by code shows the name too. */
  codeNeedsName?: boolean;
}

export type ScopeFilterName = 'state' | 'sub_market' | 'district' | 'wz_class';

/** A single-select filter the groups are shown within ('Teilmärkte in Bayern'). */
export interface GranularityScope {
  filterName: ScopeFilterName;
  valueKey: string;
}

/** Facts about a granularity that hold for every analysis and metric. */
export interface GranularityProfile {
  id: Granularity;
  traits: readonly GranularityTrait[];
  idNames: readonly IdNameMapping[];
  group: GranularityGroup;
  /** Primary scope first. */
  scope: readonly GranularityScope[];
  /** Heading words naming the groups: 'NACH BUNDESLÄNDERN'. */
  byPhrase: string;
  /** Granularity of the map beside the chart (sub-markets are mapped by state). */
  mapGranularity: Granularity;
  /** Granularity segment of dataset names (`ds__…__sm__vm`); see `DATASET_TOKEN_OVERRIDES`. */
  datasetToken: string;
}

const STATE_AND_SUB_MARKET: readonly IdNameMapping[] = ['stateCode', 'stateName', 'subMarket'];

const STATES_GROUP: GranularityGroup = { filterName: 'states', valueKey: 'state_id', codeKey: 'state_code', nameKey: 'state_name' };
const DISTRICTS_GROUP: GranularityGroup = { filterName: 'districts', valueKey: 'district_id', nameKey: 'district_name' };
const SUB_MARKETS_GROUP: GranularityGroup = { filterName: 'sub_markets', valueKey: 'sub_market_id', nameKey: 'sub_market_name' };
const WZ_CLASSES_GROUP: GranularityGroup = { filterName: 'wz_classes', valueKey: 'wz_id', codeKey: 'wz_id', nameKey: 'wz_name', codeNeedsName: true };

const STATE_SCOPE: GranularityScope = { filterName: 'state', valueKey: 'state_id' };
const SUB_MARKET_SCOPE: GranularityScope = { filterName: 'sub_market', valueKey: 'sub_market_id' };
const DISTRICT_SCOPE: GranularityScope = { filterName: 'district', valueKey: 'district_id' };
const WZ_CLASS_SCOPE: GranularityScope = { filterName: 'wz_class', valueKey: 'wz_id' };

export const GRANULARITY_PROFILES: Record<Granularity, GranularityProfile> = {
  [Granularity.States]: {
    id: Granularity.States,
    traits: [],
    idNames: STATE_AND_SUB_MARKET,
    group: STATES_GROUP,
    scope: [SUB_MARKET_SCOPE],
    byPhrase: 'NACH BUNDESLÄNDERN',
    mapGranularity: Granularity.States,
    datasetToken: 'sm',
  },
  [Granularity.WzClassStates]: {
    id: Granularity.WzClassStates,
    traits: ['wz'],
    idNames: [...STATE_AND_SUB_MARKET, 'wz'],
    group: STATES_GROUP,
    scope: [WZ_CLASS_SCOPE, SUB_MARKET_SCOPE],
    byPhrase: 'NACH BUNDESLÄNDERN',
    mapGranularity: Granularity.States,
    datasetToken: 'wz',
  },
  [Granularity.Districts]: {
    id: Granularity.Districts,
    traits: ['district-level'],
    idNames: [...STATE_AND_SUB_MARKET, 'district'],
    group: DISTRICTS_GROUP,
    scope: [STATE_SCOPE, SUB_MARKET_SCOPE],
    byPhrase: 'NACH KREISEN & KREISFR. STÄDTEN',
    mapGranularity: Granularity.Districts,
    datasetToken: 'd',
  },
  [Granularity.IndustrySubMarkets]: {
    id: Granularity.IndustrySubMarkets,
    traits: [],
    // Industries reuse `sub_market_id`, so the industry map replaces the sub-market map.
    idNames: ['stateCode', 'stateName', 'industry'],
    group: { filterName: 'industries', valueKey: 'sub_market_id', nameKey: 'sub_market_name' },
    scope: [STATE_SCOPE],
    byPhrase: 'IM BRANCHENVERGLEICH',
    mapGranularity: Granularity.States,
    datasetToken: 'id',
  },
  [Granularity.SubMarkets]: {
    id: Granularity.SubMarkets,
    traits: [],
    idNames: STATE_AND_SUB_MARKET,
    group: SUB_MARKETS_GROUP,
    scope: [STATE_SCOPE],
    byPhrase: 'NACH TEILMÄRKTEN',
    mapGranularity: Granularity.States,
    datasetToken: 'sm',
  },
  [Granularity.DistrictSubMarkets]: {
    id: Granularity.DistrictSubMarkets,
    traits: ['district-level'],
    idNames: [...STATE_AND_SUB_MARKET, 'district'],
    group: SUB_MARKETS_GROUP,
    scope: [DISTRICT_SCOPE, STATE_SCOPE],
    byPhrase: 'NACH TEILMÄRKTEN',
    mapGranularity: Granularity.Districts,
    datasetToken: 'd',
  },
  [Granularity.WzClasses]: {
    id: Granularity.WzClasses,
    traits: ['wz'],
    idNames: [...STATE_AND_SUB_MARKET, 'wz'],
    group: WZ_CLASSES_GROUP,
    scope: [STATE_SCOPE, SUB_MARKET_SCOPE],
    byPhrase: 'NACH WZ-KLASSEN',
    mapGranularity: Granularity.States,
    datasetToken: 'wz',
  },
  [Granularity.DistrictWzClasses]: {
    id: Granularity.DistrictWzClasses,
    traits: ['district-level', 'wz'],
    idNames: [...STATE_AND_SUB_MARKET, 'district', 'wz'],
    group: WZ_CLASSES_GROUP,
    scope: [DISTRICT_SCOPE, STATE_SCOPE, SUB_MARKET_SCOPE],
    byPhrase: 'NACH WZ-KLASSEN',
    mapGranularity: Granularity.Districts,
    datasetToken: 'wz_d',
  },
  // Tourism.
  [Granularity.Unknown]: {
    id: Granularity.Unknown,
    traits: [],
    idNames: STATE_AND_SUB_MARKET,
    group: STATES_GROUP,
    scope: [],
    byPhrase: 'NACH BUNDESLÄNDERN',
    mapGranularity: Granularity.States,
    datasetToken: '',
  },
};

/** View shape the family templates read for the grouped rows / bars. */
export interface GroupDetail {
  filterName: string;
  filterValueKey: string;
  filterValueLabelKey: string;
  filterValueSubLabelKey?: string;
}

/** Group detail labelled by code (compact charts) or by name (tables, rankings). */
export function groupDetailOf(granularity: Granularity, label: 'code' | 'name'): GroupDetail {
  const group = GRANULARITY_PROFILES[granularity].group;
  const detail: GroupDetail = {
    filterName: group.filterName,
    filterValueKey: group.valueKey,
    filterValueLabelKey: label === 'code' ? group.codeKey ?? group.nameKey : group.nameKey,
  };
  if (label === 'code' && group.codeNeedsName) {
    detail.filterValueSubLabelKey = group.nameKey;
  }
  return detail;
}

const ID_NAME_TRANSFORMERS: Record<
  IdNameMapping,
  (deps: Pick<DashboardConfigurationDependencies, 'localDatasetsService'>) => DashboardDataTransformer
> = {
  stateCode: (deps) => new StateIdCodeMappingDashboardDataTransformer(deps.localDatasetsService),
  stateName: (deps) => new StateIdNameMappingDashboardDataTransformer(deps.localDatasetsService),
  subMarket: (deps) => new SubMarketIdNameMappingDashboardDataTransformer(deps.localDatasetsService),
  industry: (deps) => new IndustryIdNameMappingDashboardDataTransformer(deps.localDatasetsService),
  district: (deps) => new DistrictIdNameMappingDashboardDataTransformer(deps.localDatasetsService),
  wz: (deps) => new WzIdNameMappingDashboardDataTransformer(deps.localDatasetsService),
};

/** Id → name transformers for rows of the granularity's datasets. */
export function createIdNameTransformers(
  deps: Pick<DashboardConfigurationDependencies, 'localDatasetsService'>,
  granularity: Granularity,
): DashboardDataTransformer[] {
  return GRANULARITY_PROFILES[granularity].idNames.map((mapping) => ID_NAME_TRANSFORMERS[mapping](deps));
}

const LOCATION_SCOPES: readonly ScopeFilterName[] = ['state', 'district'];
const MARKET_SCOPES: readonly ScopeFilterName[] = ['sub_market', 'wz_class'];

/** Heading word of the first scope of the given kinds; absent when the view has no such filter. */
function scopeWord(granularity: Granularity, headerWords: Map<string, any>, kinds: readonly ScopeFilterName[]): string | undefined {
  const scope = GRANULARITY_PROFILES[granularity].scope.find((candidate) => kinds.includes(candidate.filterName));
  const word = scope && headerWords.get(scope.filterName);
  return word == null || String(word).trim() === '' ? undefined : String(word);
}

/** Where the groups are (state, district) and which market they cover (sub-market, WZ class). */
export function scopeWords(granularity: Granularity, headerWords: Map<string, any>): { location?: string; market?: string } {
  return {
    location: scopeWord(granularity, headerWords, LOCATION_SCOPES),
    market: scopeWord(granularity, headerWords, MARKET_SCOPES),
  };
}

function joinWords(...parts: (string | undefined)[]): string {
  return parts.filter((part) => part != null && part.trim() !== '').join(' ');
}

/** Scope before the by-phrase: 'IN BAYERN GAMES NACH WZ-KLASSEN'. */
export function granularityHeadingPhrase(granularity: Granularity, headerWords: Map<string, any>): string {
  const { location, market } = scopeWords(granularity, headerWords);
  return joinWords(location && `IN ${location}`, market, GRANULARITY_PROFILES[granularity].byPhrase);
}

/** By-phrase first, location last: 'NACH WZ-KLASSEN GAMES, BAYERN'. */
export function granularityHeadingPhraseByFirst(granularity: Granularity, headerWords: Map<string, any>): string {
  const { location, market } = scopeWords(granularity, headerWords);
  const phrase = joinWords(GRANULARITY_PROFILES[granularity].byPhrase, market);
  return location ? `${phrase}, ${location}` : phrase;
}

/**
 * The map's words: the market, plus the map's own by-phrase when it shows other groups than the
 * chart ('GAMES NACH BUNDESLÄNDERN' beside WZ classes).
 */
export function mapHeadingPhrase(granularity: Granularity, headerWords: Map<string, any>): string {
  const profile = GRANULARITY_PROFILES[granularity];
  const map = GRANULARITY_PROFILES[profile.mapGranularity];
  const { market } = scopeWords(granularity, headerWords);
  return joinWords(market, map.group.filterName === profile.group.filterName ? undefined : map.byPhrase);
}

/** Column labelling the map's features: the map's group code (state code); none on district maps. */
export function mapLabelKeyOf(granularity: Granularity): string {
  const map = GRANULARITY_PROFILES[GRANULARITY_PROFILES[granularity].mapGranularity];
  return map.group.codeKey ?? '';
}

/** Columns identifying one series for CAGR: where it is (location scope, else state) and which group. */
export function cagrIndexColumnsOf(granularity: Granularity): string[] {
  const profile = GRANULARITY_PROFILES[granularity];
  const location = profile.scope.find((scope) => LOCATION_SCOPES.includes(scope.filterName));
  return [location?.valueKey ?? 'state_id', profile.group.valueKey];
}
